import Constants from '../Constants.js';
import Rsrc from '../rsrc/Rsrc.js';
const STATUS = Constants.GET_USERS_STATUS;
const GET = Constants.GET_USERS;
const GET_ONE = Constants.GET_USER;
const ADD = Constants.ADD_USER;
const REMOVE = Constants.REMOVE_USER;
const UPDATE = Constants.UPDATE_USER;

/**
 * makes an api call to get the list of current users for the project
 * @param {object} opts options to be provided to the api call
 * @returns {function} redux thunk function
 */
function list(opts) {
  /**
   * @returns {Promise} represents the result of the api call
   */
  return function (dispatch, getState, {
    api
  }) {
    dispatch({
      type: STATUS,
      status: Constants.STATUS_LOADING,
      statusMessage: null
    });
    return api.users().list(opts).then(data => {
      dispatch({
        type: GET,
        startIndex: data.startIndex,
        total: data.total,
        perPage: data.perPage,
        items: data.items,
        search: data.search
      });
      return data;
    }).catch(x => {
      console.log(x);
      dispatch({
        type: STATUS,
        status: Constants.STATUS_ERROR,
        statusMessage: x.message
      });
    });
  };
}

/**
 * makes an api call to create a new user 
 * @param {object} item the new user to be created
 * @returns {function} redux thunk function
 */
function create(item) {
  /**
   * @returns {Promise} represents the result of the api call
   */
  return function (dispatch, getState, {
    api
  }) {
    return api.users().create(item).then(data => {
      dispatch({
        type: ADD,
        data
      });
      return data;
    }).catch(err => {
      dispatch({
        type: STATUS,
        status: Constants.STATUS_ERROR,
        statusMessage: err
      });
    });
  };
}

/**
 * makes an api call to get a user
 * @param {string} item id of the user
 * @param {object} opts options to be inculded in the api call
 * @returns {function} redux thunk function
 */
function get(item, opts) {
  /**
   * @returns {Promise} represents the result of the api call
   */
  return function (dispatch, getState, {
    api
  }) {
    const req = api.user(item).get(opts);
    return req.then(data => {
      dispatch({
        type: GET_ONE,
        item: data
      });
      return data;
    }).catch(err => {
      dispatch({
        type: STATUS,
        status: Constants.STATUS_ERROR,
        statusMessage: err
      });
    });
  };
}

/**
 * makes an api call to remove a user
 * @param {string} item id of the user
 * @returns {function} redux thunk function
 */
function remove(item) {
  /**
   * @returns {Promise} represents the result of the api call
   */
  return function (dispatch, getState, {
    api
  }) {
    const req = api.user(item).delete();
    return req.then(data => {
      dispatch({
        type: REMOVE,
        itemId: typeof item === 'string' ? item : item.id
      });
      return data;
    }).catch(err => {
      dispatch({
        type: STATUS,
        status: Constants.STATUS_ERROR,
        statusMessage: err
      });
    });
  };
}

/**
 * makes an api call to update a user
 * @param {string} item id of the user to be updated
 * @param {object} patchOps object with the values to be updated
 * @param {object} opts any additional options to be sent to the api
 * @returns {function} redux thunk function
 */
function update(item, patchOps, opts) {
  /**
   * @returns {Promise} represents the result of the api call
   */
  return function (dispatch, getState, {
    api
  }) {
    const req = api.user(item).update(patchOps, opts);
    return req.then(data => {
      dispatch({
        type: UPDATE,
        item: data
      });
      return data;
    }).catch(err => {
      dispatch({
        type: STATUS,
        status: Constants.STATUS_ERROR,
        statusMessage: err
      });
    });
  };
}
function generateTwoFactorCode(item, method) {
  return async (dispatch, getState, {
    api
  }) => {
    try {
      const res = await api.user(item).generateTwoFactorCode(method);
      dispatch({
        type: Constants.TWO_FACTOR_GENERATE_CODE,
        method,
        info: res.message
      });
      return res;
    } catch (err) {
      dispatch({
        type: Constants.TWO_FACTOR_ERROR,
        status: Constants.STATUS_ERROR,
        statusMessage: err
      });
      throw err;
    }
  };
}
function preActivateTwoFactorMethod(item, method, data) {
  return async (dispatch, getState, {
    api
  }) => {
    try {
      const res = await api.user(item).preActivateTwoFactorMethod(method, data);
      dispatch({
        type: Constants.TWO_FACTOR_PREACTIVATE_METHOD,
        method,
        activationData: res.data
      });
      return res;
    } catch (err) {
      dispatch({
        type: Constants.TWO_FACTOR_ERROR,
        status: Constants.STATUS_ERROR,
        statusMessage: err
      });
      throw err;
    }
  };
}
function activateTwoFactorMethod(item, method, code) {
  return async (dispatch, getState, {
    api
  }) => {
    try {
      const res = await api.user(item).activateTwoFactorMethod(method, code);
      dispatch({
        type: Constants.TWO_FACTOR_ACTIVATE_METHOD,
        method,
        data: res
      });
      dispatch({
        type: UPDATE,
        item: res.user
      });
      return res;
    } catch (err) {
      dispatch({
        type: Constants.TWO_FACTOR_ERROR,
        status: Constants.STATUS_ERROR,
        statusMessage: err
      });
      throw err;
    }
  };
}
function deactivateTwoFactorMethod(item, method, data) {
  return async (dispatch, getState, {
    api
  }) => {
    try {
      const res = await api.user(item).deactivateTwoFactorMethod(method, data);
      dispatch({
        type: Constants.TWO_FACTOR_DEACTIVATE_METHOD,
        method,
        data: res
      });
      dispatch({
        type: UPDATE,
        item: res.user
      });
      return res;
    } catch (err) {
      dispatch({
        type: Constants.TWO_FACTOR_ERROR,
        status: Constants.STATUS_ERROR,
        statusMessage: err
      });
      throw err;
    }
  };
}
function deactivateTwoFactor(item, data) {
  return async (dispatch, getState, {
    api
  }) => {
    try {
      const res = await api.user(item).deactivateTwoFactor(data);
      dispatch({
        type: Constants.TWO_FACTOR_DEACTIVATE,
        data: res
      });
      dispatch({
        type: UPDATE,
        item: res.user
      });
      return res;
    } catch (err) {
      dispatch({
        type: Constants.TWO_FACTOR_ERROR,
        status: Constants.STATUS_ERROR,
        statusMessage: err
      });
      throw err;
    }
  };
}
function getOneTimeLoginCodes(item, password) {
  return async (dispatch, getState, {
    api
  }) => {
    try {
      const res = await api.user(item).getOneTimeLoginCodes(password);
      dispatch({
        type: Constants.TWO_FACTOR_GET_ONE_TIME_LOGIN_CODES,
        data: res
      });
      return res;
    } catch (err) {
      dispatch({
        type: Constants.TWO_FACTOR_ERROR,
        status: Constants.STATUS_ERROR,
        statusMessage: err
      });
      throw err;
    }
  };
}
function regenerateOneTimeLoginCodes(item, password) {
  return async (dispatch, getState, {
    api
  }) => {
    try {
      const res = await api.user(item).regenerateOneTimeLoginCodes({
        password
      });
      dispatch({
        type: Constants.TWO_FACTOR_REGENERATE_ONE_TIME_LOGIN_CODES,
        data: res
      });
      return res;
    } catch (err) {
      dispatch({
        type: Constants.TWO_FACTOR_ERROR,
        status: Constants.STATUS_ERROR,
        statusMessage: err
      });
      throw err;
    }
  };
}

/**
 * makes an api call sending an email to the provided user making them reset their password
 * @param {string} item 
 * @param {string} projectId 
 * @returns {function} redux thunk function
 */
function forcePWReset(item, projectId) {
  /**
   * @returns {Promise} represents the result of the api call
   */
  return function (dispatch, getState, {
    api
  }) {
    return new Promise((resolve, reject) => {
      api.forgotPassword(item, projectId).then(data => {
        resolve(data);
      }).error(err => {
        reject(err);
        dispatch({
          type: STATUS,
          status: Constants.STATUS_ERROR,
          statusMessage: err
        });
      });
    });
  };
}
function resource(item, path) {
  function resourceApi(dispatch, getState, opts) {
    const {
      api
    } = opts;
    return api.user(item);
  }
  return new Rsrc({
    ownerId: typeof item === 'string' ? item : item?.id,
    coownerId: null,
    path,
    resourceApi
  });
}
export default {
  list,
  create,
  get,
  remove,
  update,
  forcePWReset,
  resource,
  generateTwoFactorCode,
  preActivateTwoFactorMethod,
  activateTwoFactorMethod,
  deactivateTwoFactorMethod,
  deactivateTwoFactor,
  getOneTimeLoginCodes,
  regenerateOneTimeLoginCodes
};